import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { CALLBACK_PATH } from '@coworker/contracts';

import { ApiClient } from '../lib/api.js';
import { ApiError } from '../lib/api-error.js';
import { appConfig } from '../lib/config.js';
import { logoutUrl, redirectUri, startLogin, startSignUp, type Tokens } from './oauth.js';
import { createSessionApi, type SessionApi } from './session-api.js';
import {
  clearReturning,
  clearSilentAttempt,
  isReturning,
  markReturning,
  markSilentAttempt,
  rememberPending,
  silentAttempted,
  TokenStore,
  takePending,
} from './session-store.js';

/**
 * The session, and the two ways into it - AUTH-003, AUTH-004.
 *
 * `signedOut` is a state and not an error. The tokens live in memory only, so a
 * reload genuinely has none - `returning` says this tab had a session, which is
 * what lets `SignedOut` offer to re-enter `/oauth2/authorize` rather than
 * greeting somebody who never signed out as a stranger.
 *
 * **A reload keeps the session.** Not by storing a token - nothing is stored -
 * but by re-entering `/oauth2/authorize`, which the Cognito session cookie
 * answers with a code and no typing. See the effect below for the loop that has
 * to be closed to make that safe.
 *
 * **The access token is fifteen minutes** (`auth-stack.ts`), and since COW-483
 * there is a refresh behind it. `expired()` no longer drops the session on the
 * first 401: it asks `POST /v1/session/refresh`, which succeeds from a cookie
 * page script cannot read, and only falls back to the sign-in screen when that
 * is refused too. `restoring` is the state while that is in flight - without it
 * a reload renders the sign-in screen for a moment and the effect below can
 * redirect out from under the request.
 *
 * This supersedes COW-478, which proposed doing the refresh in the browser.
 * ADR-0039 records why it moved: a refresh token the page can read is one a
 * reload loses and a script can steal.
 */
export type AuthStatus = 'signedOut' | 'restoring' | 'signingOut' | 'signedIn';

/**
 * What happened when a 401 was met with a refresh.
 *
 * Three outcomes and not a boolean, because the middle one used to be folded
 * into the last and that signed people out: `.catch(() => false)` treated a 503
 * from the session endpoint exactly like a refused cookie. A review measured it
 * - a healthy session, one ordinary token lapse, an unavailable identity
 * provider, and the person is on the sign-in screen. This change is what
 * introduced that failure mode, so it ships with the distinction.
 */
export type RefreshOutcome =
  /** A new access token. Retry whatever was refused. */
  | 'renewed'
  /** The cookie is gone or refused. The session is over; sign in again. */
  | 'refused'
  /** The session endpoint could not answer. The session may well be fine. */
  | 'unavailable';

export interface AuthValue {
  readonly status: AuthStatus;
  /** True when a reload lost the tokens but this tab had a session - re-entry is silent. */
  readonly returning: boolean;
  readonly api: ApiClient;
  /** The id token, for the one call that needs verified email and name: AUTH-010. */
  readonly idToken: () => string | null;
  readonly signIn: (returnTo?: string) => Promise<void>;
  readonly signUp: (returnTo?: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /**
   * Call when a request answers 401. See `RefreshOutcome`: `renewed` means
   * retry, because a 401 is deliberately not retryable and nothing else will;
   * `refused` means the session is over; `unavailable` means leave it alone.
   */
  readonly expired: () => Promise<RefreshOutcome>;
  /**
   * How many times the session has been renewed in this tab.
   *
   * Read by `useRecoverSession` to tell "the token I was refused with has
   * already been replaced" from "my own new token is being refused". A counter
   * and not a timestamp, because the question is about ordering.
   */
  /**
   * Recover from a 401: refresh once, or end the session if our own new token
   * is the one being refused. `exhausted` means the latter has just happened.
   *
   * **The guard lives here, not in the caller.** It was a ref inside
   * `useRecoverSession`, and a screen mounts four of those - so each got its own
   * turn and a review measured three or four refresh round trips at the
   * fifteen-minute mark. The bound has to sit beside the counter it reads.
   */
  readonly recoverSession: () => Promise<RefreshOutcome | 'exhausted'>;
  /** Redeems the code on `/auth/callback`. Returns where to go next. */
  readonly completeSignIn: (search: string) => Promise<string>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const value = use(AuthContext);
  if (value === null) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

export interface AuthProviderProps {
  readonly children: ReactNode;
  /** Injected by tests. Production reads the values the deploy baked in. */
  readonly config?: ReturnType<typeof appConfig>;
  readonly fetchImpl?: typeof fetch;
  /** Injected so a test observes the redirect instead of navigating the runner. */
  readonly redirect?: (url: string) => void;
  readonly origin?: string;
  /**
   * Where the browser currently is. Injected for the same reason `origin` is:
   * the reload effect below has to know whether it is standing on the callback
   * route, and a test drives a memory router whose path `globalThis.location`
   * knows nothing about.
   */
  readonly currentPath?: string;
  /**
   * A session to start from. Only a test passes this - production has no tokens
   * until the callback redeems a code - and it exists so a route test can render
   * a signed-in tree without driving a redirect it cannot follow.
   */
  readonly initialTokens?: Tokens;
}

export function AuthProvider({
  children,
  config = appConfig(),
  fetchImpl,
  redirect = (url) => globalThis.location.assign(url),
  origin = globalThis.location.origin,
  currentPath = `${globalThis.location.pathname}${globalThis.location.search}`,
  initialTokens,
}: AuthProviderProps) {
  const store = useRef<TokenStore>(undefined as unknown as TokenStore);
  if (store.current === undefined) {
    store.current = new TokenStore();
    if (initialTokens !== undefined) store.current.set(initialTokens);
  }

  const [returning] = useState(() => isReturning());
  const [status, setStatus] = useState<AuthStatus>(() => {
    if (initialTokens !== undefined) return 'signedIn';
    // **Not gated on `returning`, and a review is why.** `isReturning()` reads
    // `sessionStorage`, which does not survive a closed tab - so gating on it
    // made a twenty-four-hour cookie reachable only from a tab that had never
    // been closed. Measured: valid cookie, healthy endpoint, new tab, and the
    // app made *zero* requests and showed a sign-in screen. That is not the
    // session this ticket exists to deliver.
    //
    // The cost of asking speculatively is one 401 for somebody who has never
    // signed in - against a route that touches no database. The redirect below
    // stays gated on `returning`, because a whole hosted-UI round trip is not
    // something to spend on a guess.
    return currentPath.split('?')[0] === CALLBACK_PATH ? 'signedOut' : 'restoring';
  });

  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: config.apiUrl,
        accessToken: () => store.current.accessToken(),
        ...(fetchImpl === undefined ? {} : { fetchImpl }),
      }),
    [config.apiUrl, fetchImpl],
  );

  const session: SessionApi = useMemo(() => createSessionApi(api), [api]);

  const begin = useCallback(
    async (start: typeof startLogin, returnTo: string) => {
      const { url, verifier, state } = await start(config, origin);
      rememberPending({ verifier, state, returnTo });
      redirect(url);
    },
    [config, origin, redirect],
  );

  const signIn = useCallback((returnTo = '/') => begin(startLogin, returnTo), [begin]);

  const signUp = useCallback((returnTo = '/') => begin(startSignUp, returnTo), [begin]);

  // One refresh at a time, and a generation so a caller can tell whether the
  // token it was refused with has already been replaced.
  const inFlight = useRef<Promise<RefreshOutcome> | null>(null);
  const generation = useRef(0);

  /**
   * Trade the cookie for a new access token.
   *
   * **Single-flight.** Three queries can fail with 401 in the same tick, and
   * `main.tsx` runs the app in StrictMode so every effect fires twice in
   * development. Each starting its own refresh would spend the cookie several
   * times over - and a rotating refresh token is single-use. Later callers await
   * the promise the first one started.
   *
   * **The generation is the loop breaker**, and it replaced a wall clock. A
   * ten-second cooldown bounded the loop but keyed on the wrong thing: it could
   * not tell "this 401 came from the token I just replaced" from "a second
   * screen was refused nine seconds later", and the second is ordinary. What
   * callers actually need to know is whether a refresh has happened since the
   * request that was refused - which is a counter, not a timer.
   */
  const refresh = useCallback(async (): Promise<RefreshOutcome> => {
    inFlight.current ??= session
      .refresh()
      .then((tokens) => {
        store.current.set(tokens);
        generation.current += 1;
        return 'renewed' as const;
      })
      .catch((error: unknown) =>
        // Only a refusal ends a session. `isUnauthenticated` is the same
        // discriminator `use-session.ts` and `Onboarding.tsx` already read.
        error instanceof ApiError && error.isUnauthenticated
          ? ('refused' as const)
          : ('unavailable' as const),
      )
      .finally(() => {
        inFlight.current = null;
      });

    return inFlight.current;
  }, [session]);

  const expired = useCallback(async (): Promise<RefreshOutcome> => {
    const outcome = await refresh();

    if (outcome === 'renewed') {
      setStatus('signedIn');
    } else if (outcome === 'refused') {
      store.current.clear();
      setStatus('signedOut');
    }
    // `unavailable` deliberately changes nothing: the tokens may still be good,
    // and the caller renders the retryable error card it already has.

    return outcome;
  }, [refresh]);

  /**
   * The generation at which a recovery last minted a token.
   *
   * One ref for the whole tab. If we are asked again while it is still current,
   * the token we just minted is the one being refused - another refresh would
   * mint another and meet the same 401.
   */
  const recoveredAt = useRef<number | null>(null);

  const endSession = useCallback(() => {
    store.current.clear();
    setStatus('signedOut');
  }, []);

  const recoverSession = useCallback(async (): Promise<RefreshOutcome | 'exhausted'> => {
    if (recoveredAt.current === generation.current) {
      // Doing nothing here would strand the person on an error card whose
      // "Try again" re-sends the token that is being refused.
      //
      // **This branch ends a session, so read the ordering paragraph in
      // `hooks/use-recover-session.ts` before touching either.** A 401 that
      // arrived from a request still carrying the *old* token would trip this
      // and sign somebody out mid-session. What makes that unreachable is the
      // order of two lines in the caller, not anything here.
      endSession();
      return 'exhausted';
    }

    const outcome = await expired();
    if (outcome === 'renewed') recoveredAt.current = generation.current;
    return outcome;
  }, [expired, endSession]);

  const signingOut = useRef(false);

  const signOut = useCallback(async (): Promise<void> => {
    // Repeat clicks used to send a DELETE each, because nothing on screen
    // changed for as long as the request took and the button stayed live.
    if (signingOut.current) return;
    signingOut.current = true;
    setStatus('signingOut');

    try {
      // Awaited before the redirect: `redirect` navigates, and a navigation
      // cancels an in-flight request, so a fire-and-forget DELETE would mean
      // sign-out silently never revoked anything. Bounded well under the client
      // default, because this route calls Cognito's revoke and touches no
      // database - twelve seconds of a dead-looking button is worse than a
      // revoke that did not land.
      await session.end().catch(() => undefined);
    } finally {
      store.current.clear();
      clearReturning();
      clearSilentAttempt();
      setStatus('signedOut');
      signingOut.current = false;
      redirect(logoutUrl(config, origin));
    }
  }, [config, origin, redirect, session]);

  // A reload has no tokens - they were only ever in memory - but it does have
  // the cookie, so the session can be picked up without leaving the page at
  // all. This runs before the hosted-UI re-entry below and usually means it
  // never fires: a redirect round trip is the fallback now, not the mechanism.
  useEffect(() => {
    if (status !== 'restoring') return;

    void (async () => {
      // Every outcome but `renewed` ends in `signedOut` here, and that is not
      // the same judgement `expired()` makes. There is no session to preserve at
      // mount: with no access token the app cannot proceed either way, and
      // `signedOut` is what lets the hosted-UI fallback below have its turn.
      // Written as a comparison rather than a truth test - `'refused'` and
      // `'unavailable'` are both truthy strings, which is a bug this had.
      setStatus((await refresh()) === 'renewed' ? 'signedIn' : 'signedOut');
    })();
  }, [status, refresh]);

  // The fallback, for when our own session cookie is gone or was never set -
  // the effect above is the mechanism now. A reload has no tokens, because they
  // were never anywhere but memory; the Cognito session cookie outlives the
  // page, so re-entering `/oauth2/authorize` comes straight back with a code
  // and nobody types anything.
  //
  // **`prompt=none` is the documented way to make this safe, and Cognito's
  // hosted UI does not implement it.** Measured against the deployed pool on
  // 2026-08-30: with no session cookie, `/oauth2/authorize?...&prompt=none`
  // answers 302 to its own `/login` page rather than returning
  // `error=login_required` on the callback. So there is no way to ask "is there
  // a session" without risking a visible login page, and the loop that creates
  // is closed by `silentAttempted` instead: set before leaving, cleared only
  // when a code redeems. One attempt per tab. A person who backs out of the
  // hosted UI lands on our own sign-in screen and stays there.
  //
  // Deliberately not fired by `expired()`. That would re-enter every fifteen
  // minutes, and an API that answered 401 to a freshly minted token would cycle
  // between the two forever. COW-483 made that path silent and bounded - the
  // refresh, and the generation guard in `recoverSession`, which exists because
  // the refresh revived exactly this loop in a new shape. This effect is still
  // only the reload, and only when the refresh could not do it.
  useEffect(() => {
    if (status !== 'signedOut' || !returning || silentAttempted()) return;
    // The callback route is mid-redemption and has no tokens *yet*. Redirecting
    // out from under it would discard the code it is holding.
    if (currentPath.split('?')[0] === CALLBACK_PATH) return;

    markSilentAttempt();
    void begin(startLogin, currentPath);
  }, [status, returning, begin, currentPath]);

  const completeSignIn = useCallback(
    async (search: string) => {
      const params = new URLSearchParams(search);

      // Cognito reports its own failures on the redirect rather than the token
      // endpoint - a cancelled consent screen arrives here, not as an exception.
      const failure = params.get('error');
      if (failure !== null) {
        throw new Error(`${failure}: ${params.get('error_description') ?? 'no description'}`);
      }

      const code = params.get('code');
      if (code === null) throw new Error('the callback carried no authorization code');

      const pending = takePending();
      if (pending === null) {
        throw new Error('no login was started in this tab, so there is no verifier to redeem with');
      }
      // Without this, a code from somewhere else redeems in this tab.
      if (params.get('state') !== pending.state) {
        throw new Error('the callback state does not match the login this tab started');
      }

      // The redemption happens in our API, which keeps the refresh token in a
      // cookie and hands back only what the browser may hold. Everything above
      // this line stays here: the verifier and the state are facts about *this
      // tab*, which no server can know.
      const tokens: Tokens = await session.create({
        code,
        codeVerifier: pending.verifier,
        redirectUri: redirectUri(origin),
      });
      store.current.set(tokens);
      markReturning();
      // Only here, and only on a code that really redeemed: the flag is what
      // stops a dead hosted-UI session from bouncing the next reload straight
      // back out again.
      clearSilentAttempt();
      setStatus('signedIn');
      return pending.returnTo;
    },
    [origin, session],
  );

  const value = useMemo<AuthValue>(
    () => ({
      status,
      returning,
      api,
      idToken: () => store.current.idToken(),
      recoverSession,
      signIn,
      signUp,
      signOut,
      expired,
      completeSignIn,
    }),
    [status, returning, api, signIn, signUp, signOut, expired, recoverSession, completeSignIn],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
